import { compact } from 'lodash';
import { useMixpanel } from 'react-mixpanel-browser';

import { useActiveOtaPlans } from '@/api/ota/useDeviceOtaDetail';
import { useSubscriptionTagsInfo } from '@/api/subscriptionTags';
import { SubscriptionTagInfo } from '@/api/subscriptionTags/types';
import { Null } from '@/components/Null';
import { USB_INSTRUCTIONS_HREF } from '@/components/spotter/statusTab/firmwareUpdateAlertParts';
import { InlineAlert, InlineAlertBody, InlineAlertPrimaryButton, InlineAlertTitle } from '@/components/ui/InlineAlert';
import { itemQuantity } from '@/utils/languageHelpers';

const TESTID = 'ota-in-progress-alert';

export type UpdatingDevice = Pick<SubscriptionTagInfo, 'spotter_id' | 'spotter_name'> & { otaPlanId: number };

export type TelemetryOtaAlertText =
    | { action: 'trackUpdate'; title: string; body: string; device: UpdatingDevice }
    | { action: 'learnMore'; title: string; body: string };

export const getTelemetryOtaAlertText = (updatingDevices: UpdatingDevice[]): TelemetryOtaAlertText | null => {
    if (!updatingDevices.length) {
        return null;
    }

    if (updatingDevices.length === 1) {
        const [device] = updatingDevices;
        const { spotter_id, spotter_name } = device;
        const displayName = spotter_name || spotter_id;
        const deviceLabel = displayName === spotter_id ? spotter_id : `${displayName} (${spotter_id})`;

        return {
            action: 'trackUpdate',
            title: `${deviceLabel} is currently updating`,
            body: "You will not be able to make any changes to your system's telemetry until this update process is complete.",
            device,
        };
    }

    return {
        action: 'learnMore',
        title: `${itemQuantity(updatingDevices.length, 'device')} are currently updating`,
        body: "You will not be able to make any changes to these systems' telemetry until their update processes are complete.",
    };
};

export const TelemetryOtaInProgressAlert = () => {
    const mixpanel = useMixpanel();
    const { data: subscriptionTagsInfo } = useSubscriptionTagsInfo();
    const subscriptionTags = subscriptionTagsInfo?.data;

    const activeOtaPlans = useActiveOtaPlans(subscriptionTags?.map(({ spotter_id }) => spotter_id) ?? []);

    const updatingDevices = compact(
        (subscriptionTags ?? []).map(({ spotter_id, spotter_name }) => {
            const otaPlan = activeOtaPlans[spotter_id];
            return otaPlan ? { spotter_id, spotter_name, otaPlanId: otaPlan.id } : null;
        })
    );
    const otaAlertText = getTelemetryOtaAlertText(updatingDevices);

    if (!otaAlertText) {
        return <Null data-testid={`${TESTID}-hidden`} />;
    }

    return (
        <InlineAlert
            severity="info"
            actionButton={
                otaAlertText.action === 'trackUpdate' ? (
                    <InlineAlertPrimaryButton
                        testid={TESTID}
                        href={`/spotter/${otaAlertText.device.spotter_id}#status`}
                        onClick={() => {
                            mixpanel?.track('Click OTA in progress track update', {
                                spotterId: otaAlertText.device.spotter_id,
                                otaPlanId: otaAlertText.device.otaPlanId,
                            });
                        }}
                    >
                        Track update
                    </InlineAlertPrimaryButton>
                ) : (
                    <InlineAlertPrimaryButton testid={TESTID} variant="outlined" href={USB_INSTRUCTIONS_HREF}>
                        Learn more
                    </InlineAlertPrimaryButton>
                )
            }
        >
            <InlineAlertTitle testid={TESTID}>{otaAlertText.title}</InlineAlertTitle>
            <InlineAlertBody testid={TESTID}>{otaAlertText.body}</InlineAlertBody>
        </InlineAlert>
    );
};
